<template>
  <input
    ref="$inputRef"
    v-imask="mask"
    :class="$style.element"
    :value="model"
    @input="onInputDebounced"
    @change="onChange"
  />
</template>

<script lang="ts" setup>
import { useTemplateRef, computed, onMounted } from 'vue'
import type { FactoryArg } from 'imask'
import { IMaskDirective as vImask } from 'vue-imask'
import debounceFunc from 'lodash/debounce'

const props = withDefaults(
  defineProps<{
    autofocus?: boolean
    debounce?: number
    lazy?: boolean
    mask?: FactoryArg
  }>(),
  {
    autofocus: false,
    debounce: undefined,
    lazy: false,
    mask: undefined
  }
)

const model = defineModel<string>()

const $inputRef = useTemplateRef<HTMLInputElement>('$inputRef')

const focus = () => {
  $inputRef.value?.focus()
}

const blur = () => {
  $inputRef.value?.blur()
}

const onInput = (evt: Event) => {
  if (!props.lazy) {
    model.value = (evt.target as HTMLInputElement).value
  }
}

const onChange = (evt: Event) => {
  if (props.lazy) {
    model.value = (evt.target as HTMLInputElement).value
  }
}

const onInputDebounced = computed(() => debounceFunc(onInput, props.debounce))

onMounted(() => {
  if (props.autofocus) {
    focus()
  }
})

defineExpose({ focus, blur })
</script>

<style lang="scss" module>
.element {
  outline: none;
  border: none;
  background: none;

  // see https://stackoverflow.com/questions/2781549/removing-input-background-colour-for-chrome-autocomplete
  &:-webkit-autofill {
    transition-property: color, background;
    transition-delay: 9999s;
  }

  &:read-only {
    text-overflow: ellipsis;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }

  &[type='search'] {
    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration {
      -webkit-appearance: none;
    }
  }
}
</style>
